import type { WorkflowScheduleRecurrenceKind } from '@/types'

const FALLBACK_TIME_ZONES = [
  'UTC',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Bucharest',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Asia/Kolkata',
  'Asia/Tokyo',
  'Australia/Sydney',
]

export function getBrowserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

export function getSupportedTimeZones(): string[] {
  let zones: string[] = []
  try {
    if (typeof Intl.supportedValuesOf === 'function') {
      zones = Intl.supportedValuesOf('timeZone')
    }
  } catch {
    zones = []
  }
  const all = new Set(zones.length > 0 ? zones : FALLBACK_TIME_ZONES)
  all.add('UTC')
  all.add(getBrowserTimeZone())
  return [...all].sort((a, b) => a.localeCompare(b))
}

export function describeRecurrence(
  kind: WorkflowScheduleRecurrenceKind,
  cronExpression: string | null | undefined,
  intervalMinutes: number | null | undefined,
): string {
  if (kind === 'Interval') {
    return intervalMinutes ? `Every ${intervalMinutes} min` : 'Interval'
  }
  return cronExpression ? `Cron ${cronExpression}` : 'Cron'
}

export function formatShortInTimeZone(utc: string | null | undefined, timeZone: string): string {
  if (!utc) return '-'
  const date = new Date(utc)
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone,
    }).format(date)
  } catch {
    return date.toISOString()
  }
}

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['day', 86_400],
  ['hour', 3_600],
  ['minute', 60],
]

export function formatRelativeToNow(utc: string | null | undefined, now: number = Date.now()): string {
  if (!utc) return '-'
  const seconds = Math.round((new Date(utc).getTime() - now) / 1000)
  if (Math.abs(seconds) < 60) return 'just now'
  const [unit, size] = RELATIVE_UNITS.find(([, s]) => Math.abs(seconds) >= s)!
  return new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }).format(Math.round(seconds / size), unit)
}

export function formatInTimeZone(utc: string | null | undefined, timeZone: string): string {
  if (!utc) return '-'
  const date = new Date(utc)
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone,
      timeZoneName: 'short',
    }).format(date)
  } catch {
    return date.toISOString()
  }
}
